import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { Notification, Application } from '../types';
import { 
  Plus, 
  Filter, 
  Bell, 
  Clock, 
  CheckCircle,
  XCircle,
  Eye,
  X,
  Loader2,
  Send
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function NotificationsPage() {
  const [selectedApp, setSelectedApp] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [viewingNotification, setViewingNotification] = useState<Notification | null>(null);

  const { data: apps } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.getApplications(1, 100),
  });

  const { data: notifications, isLoading } = useQuery({
    queryKey: ['notifications', selectedApp],
    queryFn: () => api.getNotifications(selectedApp || undefined, 1, 50),
    refetchInterval: 10000,
  });

  const getStatusColor = (status: Notification['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400';
      case 'partially_completed':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'failed':
        return 'bg-red-500/20 text-red-400';
      case 'processing':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="w-full md:w-64">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <select
              value={selectedApp}
              onChange={(e) => setSelectedApp(e.target.value)}
              className="input pl-10 appearance-none"
            >
              <option value="">All Applications</option>
              {apps?.data?.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button onClick={() => setShowModal(true)} className="btn btn-primary flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Send Notification
        </button>
      </div>

      {/* Notifications List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {notifications?.data?.map((notification) => (
            <div key={notification.id} className="card hover:border-slate-600 transition-colors">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-slate-700 rounded-lg">
                  <Bell className="h-6 w-6 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-white">{notification.title}</h3>
                      <p className="text-sm text-slate-400 mt-1 line-clamp-2">{notification.body}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(notification.status)}`}>
                        {notification.status.replace('_', ' ')}
                      </span>
                      <button
                        onClick={() => setViewingNotification(notification)}
                        className="btn btn-secondary p-2"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      notification.type === 'transactional' 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-orange-500/20 text-orange-400'
                    }`}>
                      {notification.type}
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-green-400" />
                      {notification.successCount} sent
                    </span>
                    {notification.failureCount > 0 && (
                      <span className="flex items-center gap-1">
                        <XCircle className="h-4 w-4 text-red-400" />
                        {notification.failureCount} failed
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {notifications?.data?.length === 0 && (
            <div className="card text-center py-12">
              <Bell className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No notifications yet</p>
            </div>
          )}
        </div>
      )}

      {/* Send Notification Modal */}
      {showModal && (
        <SendNotificationModal
          applications={apps?.data || []}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* View Notification Modal */}
      {viewingNotification && (
        <NotificationDetailModal
          notification={viewingNotification}
          onClose={() => setViewingNotification(null)}
        />
      )}
    </div>
  );
}

interface SendNotificationModalProps {
  applications: Application[];
  onClose: () => void;
}

function SendNotificationModal({ applications, onClose }: SendNotificationModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    applicationId: applications[0]?.id || '',
    title: '',
    body: '',
    icon: '',
    url: '',
    type: 'transactional' as 'transactional' | 'marketing',
    userId: '',
    topics: '',
  });

  const sendMutation = useMutation({
    mutationFn: () => api.sendNotification({
      applicationId: formData.applicationId,
      title: formData.title,
      body: formData.body,
      icon: formData.icon || undefined,
      url: formData.url || undefined,
      type: formData.type,
      filters: {
        userId: formData.userId || undefined,
        topics: formData.topics ? formData.topics.split(',').map(t => t.trim()) : undefined,
      },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Send Notification</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {sendMutation.error && (
            <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {sendMutation.error instanceof Error ? sendMutation.error.message : 'An error occurred'}
            </div>
          )}

          <div>
            <label className="label">Application *</label>
            <select
              value={formData.applicationId}
              onChange={(e) => setFormData({ ...formData, applicationId: e.target.value })}
              className="input"
              required
            >
              {applications.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input"
              placeholder="Notification title"
              required
            />
          </div>

          <div>
            <label className="label">Body *</label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              className="input min-h-[80px]"
              placeholder="Notification message"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Icon URL</label>
              <input
                type="url"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                className="input"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="label">Click URL</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="input"
                placeholder="https://..."
              />
            </div>
          </div>

          <div>
            <label className="label">Type</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  value="transactional"
                  checked={formData.type === 'transactional'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'transactional' })}
                  className="text-blue-500"
                />
                <span className="text-sm text-slate-300">Transactional (high priority)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="type"
                  value="marketing"
                  checked={formData.type === 'marketing'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'marketing' })}
                  className="text-blue-500"
                />
                <span className="text-sm text-slate-300">Marketing (batched)</span>
              </label>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-4">
            <p className="text-sm text-slate-400 mb-3">Target Filters (optional)</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">User ID</label>
                <input
                  type="text"
                  value={formData.userId}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  className="input"
                  placeholder="user_123"
                />
              </div>
              <div>
                <label className="label">Topics (comma-separated)</label>
                <input
                  type="text"
                  value={formData.topics}
                  onChange={(e) => setFormData({ ...formData, topics: e.target.value })}
                  className="input"
                  placeholder="news, alerts"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              disabled={sendMutation.isPending}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {sendMutation.isPending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Send Notification
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface NotificationDetailModalProps {
  notification: Notification;
  onClose: () => void;
}

function NotificationDetailModal({ notification, onClose }: NotificationDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="card w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Notification Details</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-slate-400">Title</label>
            <p className="text-white">{notification.title}</p>
          </div>
          <div>
            <label className="text-sm text-slate-400">Body</label>
            <p className="text-white">{notification.body}</p>
          </div>
          {notification.url && (
            <div>
              <label className="text-sm text-slate-400">URL</label>
              <p className="text-blue-400 break-all">{notification.url}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400">Status</label>
              <p className="text-white capitalize">{notification.status.replace('_', ' ')}</p>
            </div>
            <div>
              <label className="text-sm text-slate-400">Type</label>
              <p className="text-white capitalize">{notification.type}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-slate-400">Success Count</label>
              <p className="text-green-400 text-lg font-semibold">{notification.successCount}</p>
            </div>
            <div>
              <label className="text-sm text-slate-400">Failure Count</label>
              <p className="text-red-400 text-lg font-semibold">{notification.failureCount}</p>
            </div>
          </div>
          <div>
            <label className="text-sm text-slate-400">Created At</label>
            <p className="text-white">{new Date(notification.createdAt).toLocaleString()}</p>
          </div>
          {notification.sentAt && (
            <div>
              <label className="text-sm text-slate-400">Sent At</label>
              <p className="text-white">{new Date(notification.sentAt).toLocaleString()}</p>
            </div>
          )}
        </div>

        <button onClick={onClose} className="btn btn-secondary w-full mt-6">
          Close
        </button>
      </div>
    </div>
  );
}
